import { Dialog, Toast } from '@akpl/ui-tv';
import { useEffect, useSyncExternalStore } from 'react';

import { useStore } from '../store';
import { useT } from '../useT';
import styles from './shell.module.css';

/** Renders `ui.dialog` with the design-system Dialog (focus trap + Back → close). */
export function DialogHost() {
  const dialog = useStore((s) => s.ui.dialog);
  const closeDialog = useStore((s) => s.closeDialog);
  if (!dialog) return null;
  return (
    <Dialog
      open
      focusKey={`dialog-${dialog.id}`}
      title={dialog.title}
      body={dialog.body}
      actions={dialog.actions}
      onClose={dialog.onClose ?? closeDialog}
    />
  );
}

/** Shows the newest toast and expires it after its TTL. */
export function ToastHost() {
  const toasts = useStore((s) => s.ui.toasts);
  const dismiss = useStore((s) => s.dismissToast);
  const current = toasts[toasts.length - 1];
  useEffect(() => {
    if (!current) return undefined;
    const id = window.setTimeout(() => dismiss(current.id), current.ttlMs);
    return () => window.clearTimeout(id);
  }, [current, dismiss]);
  return <Toast message={current?.message ?? ''} tone={current?.tone} visible={Boolean(current)} />;
}

/**
 * `navigator.onLine`, as a subscription — the same signal the phone's banner uses
 * (`apps/mobile/src/ui/OfflineBanner.tsx`).
 */
function useOnline(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('online', onChange);
      window.addEventListener('offline', onChange);
      return () => {
        window.removeEventListener('online', onChange);
        window.removeEventListener('offline', onChange);
      };
    },
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    () => true,
  );
}

/**
 * "Sin conexión" / "Reconectando…" (docs/04 §5, UX-094).
 *
 * `ui.reconnecting` is written from Firebase `.info/connected` after a 5 s grace, and that is
 * the only thing this banner used to read. It is the *slowest* signal there is: the RTDB socket
 * survives a link that has already gone, so twelve seconds with the TV's network cut produced
 * no banner, no chip and no change to a single character on screen — a room whose TV had
 * silently stopped receiving its phones' songs was told nothing at all. `navigator.onLine`
 * flips immediately and is the signal the phone companion already shows, so the TV shows it
 * too, and the socket-level "reconnecting" stays as the second line of defence for the case
 * where the TV is on a network that itself cannot reach us.
 */
export function ReconnectBanner() {
  const { t } = useT();
  const online = useOnline();
  const reconnecting = useStore((s) => s.ui.reconnecting);
  const state = !online ? 'offline' : reconnecting ? 'reconnecting' : null;
  if (!state) return null;
  return (
    <div className={styles.banner} role="status" data-testid="offline-banner" data-state={state}>
      {state === 'offline'
        ? `${t('common:states.offline')} · ${t('tv:offline.body')}`
        : t('tv:offline.banner')}
    </div>
  );
}
